import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Menu from '../main/menu';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc, getDoc, query, where, getDocs, serverTimestamp, onSnapshot, limit, deleteDoc } from 'firebase/firestore';
import './styles/Organization.css';
import { useLanguage } from '../contexts/LanguageContext';

const Organization = ({ onLogout }) => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [members, setMembers] = useState([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState('employee');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isBuyLicenseModalOpen, setIsBuyLicenseModalOpen] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [isAssignCustomerModalOpen, setIsAssignCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [employeesList, setEmployeesList] = useState([]);
  const [organizationLicenses, setOrganizationLicenses] = useState([]);
  const [stripeTotalLicenses, setStripeTotalLicenses] = useState(0);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);

  // Hent abonnementsdata
  const fetchSubscriptionData = async () => {
    if (!currentUser) return;

    try {
      console.log("Organization.js: Henter abonnementsdata for", currentUser?.uid);
      console.log("Organization.js: Brugerrolle", memberRole);

      const response = await fetch(
        `${process.env.REACT_APP_STRIPE_CHECK_SUBSCRIPTION_URL}/${currentUser.uid}?email=${encodeURIComponent(currentUser.email)}`
      );
      
      if (!response.ok) {
        throw new Error(`Server svarede med status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Abonnementsdata:', data);
      console.log('Har allSubscriptions:', !!data.allSubscriptions);
      if (data.allSubscriptions) {
        console.log('Antal abonnementer i allSubscriptions:', data.allSubscriptions.length);
        console.log('Detaljer om abonnementer:', data.allSubscriptions);
      }
      
      setSubscriptionData(data);
      
      // Gem det totale antal licenser fra Stripe
      if (data.allSubscriptions) {
        const stripeSeats = data.allSubscriptions.reduce((total, sub) => 
          total + (sub.seats || 1), 0);
        setStripeTotalLicenses(stripeSeats);
        console.log(`Beregnet ${stripeSeats} totale licenser fra alle abonnementer`);
      } else {
        setStripeTotalLicenses(data.totalSeats || 0);
        console.log(`Fandt ${data.totalSeats || 0} totale licenser fra totalSeats`);
      }
      
      // Hvis vi har et abonnement, forsøg at oprette en organisation hvis brugeren ikke allerede har en
      if (data.hasAccess) {
        await checkAndCreateOrganization();
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Fejl ved hentning af abonnementsdata:', error);
      setError(t('organization', 'notifications.error.general'));
      setLoading(false);
    }
  };

  // Tjek om bruger har en organisation og opret en hvis ikke
  const checkAndCreateOrganization = async () => {
    try {
      console.log("Organization.js: Kontrollerer organisation");

      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      let orgId = userDoc.data().organizationId;
      
      if (!orgId) {
        // Bruger har ikke en organisation, opret en ny
        const orgRef = await addDoc(collection(db, 'organizations'), {
          name: `${userDoc.data().name || currentUser.displayName || currentUser.email}'s Organisation`,
          ownerId: currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        orgId = orgRef.id;
        
        // Tilføj brugeren selv som admin i organisationen
        await addDoc(collection(db, 'organizations', orgId, 'members'), {
          userId: currentUser.uid,
          email: currentUser.email,
          role: 'admin',
          name: userDoc.data().name || currentUser.displayName || currentUser.email.split('@')[0],
          status: 'active',
          joinedAt: serverTimestamp()
        });
        
        // Opret og tildel den første licens til den nuværende bruger
        await addDoc(collection(db, 'organizations', orgId, 'licenses'), {
          type: 'basic',
          assignedTo: currentUser.uid,
          assignedEmail: currentUser.email,
          assignedAt: serverTimestamp(),
          status: 'active',
          createdAt: serverTimestamp()
        });
        
        // Opdater brugerens dokument med organisationsid
        await updateDoc(doc(db, 'users', currentUser.uid), {
          organizationId: orgId,
          organizationRole: 'admin',
          userType: 'employee'  // Tilføj dette felt for at sikre admin-status
        });
        
        console.log('Ny organisation oprettet:', orgId);
      }
      
      setOrganizationId(orgId);
      setIsOrgAdmin(true);  // Tilføj dette for at sikre admin-status vises
      fetchTeamMembers(orgId);
      fetchOrganizationLicenses(orgId);
    } catch (error) {
      console.error('Fejl ved tjek/oprettelse af organisation:', error);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, [currentUser]);

  const fetchTeamMembers = async (orgId) => {
    if (!orgId) return;
    
    try {
      setLoading(true);
      console.log("Henter medlemmer for organisation:", orgId);
      
      // Opsæt real-time lytter til medlemmer
      const membersRef = collection(db, 'organizations', orgId, 'members');
      const unsubscribe = onSnapshot(membersRef, (snapshot) => {
        const membersList = [];
        const customersListTemp = [];
        const employeesListTemp = [];
        
        console.log(`Fandt ${snapshot.size} medlemmer i databasen`);
        
        snapshot.forEach(doc => {
          const memberData = {
            id: doc.id,
            ...doc.data(),
            isMember: true
          };
          
          console.log("Fundet medlem:", memberData.email, "Status:", memberData.status);
          
          // VIS ALLE MEDLEMMER, OGSÅ MED STATUS 'removed' - vi håndterer dem senere
          membersList.push(memberData);
          
          // Opdel efter type (men ikke efter status her)
          if (memberData.userType === 'customer') {
            customersListTemp.push(memberData);
          } else {
            employeesListTemp.push(memberData);
          }
        });
        
        // Tilføj også inviterede medlemmer
        const invitationsQuery = query(
          collection(db, 'organizations', orgId, 'invitations')
          // FJERN FILTRERINGEN PÅ 'sent' FOR AT SE ALLE INVITATIONER
        );
        
        getDocs(invitationsQuery).then((invitationsSnapshot) => {
          const pendingMembers = [];
          const pendingCustomers = [];
          
          console.log(`Fandt ${invitationsSnapshot.size} invitationer i databasen`);
          
          invitationsSnapshot.forEach(doc => {
            const invitationData = {
              id: doc.id,
              ...doc.data(),
              status: doc.data().status || 'Inviteret',
              isInvitation: true
            };
            
            console.log("Fundet invitation:", invitationData.email, "Status:", invitationData.status);
            
            // Inkluder alle invitationer til debugging
            pendingMembers.push(invitationData);
            
            if (invitationData.userType === 'customer') {
              pendingCustomers.push(invitationData);
            }
          });
          
          // Sørg for at den aktuelle bruger også vises i den samlede medlemsliste
          let currentUserInList = false;
          const updatedMembersList = membersList.map(member => {
            if (member.email === currentUser.email || member.userId === currentUser.uid) {
              currentUserInList = true;
              return {
                ...member,
                isCurrentUser: true
              };
            }
            return member;
          });
          
          // Hvis den nuværende bruger ikke allerede er på listen, tilføj dem
          if (!currentUserInList) {
            const currentUserData = {
              id: 'current-user',
              email: currentUser.email,
              name: currentUser.displayName || currentUser.email.split('@')[0],
              role: 'admin',
              status: 'active',
              isCurrentUser: true,
              userId: currentUser.uid
            };
            
            updatedMembersList.push(currentUserData);
            employeesListTemp.push(currentUserData);
          }
          
          // Fjern medlemmer med status 'removed'
          const visibleMembersList = updatedMembersList.filter(member => 
            member.status !== 'removed'
          );
          
          // Fjern invitationer for brugere der allerede er aktive medlemmer
          const activeEmails = visibleMembersList
            .filter(member => member.status === 'active')
            .map(member => member.email.toLowerCase());
          
          const filteredPendingMembers = pendingMembers.filter(invitation => 
            !activeEmails.includes(invitation.email.toLowerCase())
          );
          
          // Kombiner de to lister
          const finalMembersList = [...visibleMembersList, ...filteredPendingMembers];
          console.log("Total antal synlige medlemmer + invitationer:", finalMembersList.length);
          
          // Opdater de tre lister
          setMembers(finalMembersList);
          setCustomersList([...customersListTemp, ...pendingCustomers]);
          setEmployeesList(employeesListTemp);
          
          // Hent eventuelle kundetildelinger
          fetchCustomerAssignments(orgId, [...customersListTemp, ...pendingCustomers]);
          
          setLoading(false);
        });
      });
      
      // Returner unsubscribe funktion
      return unsubscribe;
    } catch (error) {
      console.error('Fejl ved hentning af teammedlemmer:', error);
      setError(t('organization', 'notifications.error.general'));
      setLoading(false);
    }
  };

  const fetchCustomerAssignments = async (orgId, customers) => {
    if (!orgId || customers.length === 0) return;
    
    try {
      const assignmentsRef = collection(db, 'organizations', orgId, 'customerAssignments');
      const assignmentsSnapshot = await getDocs(assignmentsRef);
      
      if (assignmentsSnapshot.empty) return;
      
      // Opret et map af tildelinger
      const assignments = {};
      assignmentsSnapshot.forEach(doc => {
        const data = doc.data();
        assignments[data.customerId] = {
          id: doc.id,
          ...data
        };
      });
      
      // Opdater kundelisten med tildelinger
      const updatedCustomers = customers.map(customer => {
        const assignment = assignments[customer.id] || assignments[customer.userId];
        if (assignment) {
          return {
            ...customer,
            assignmentId: assignment.id,
            assignedTo: assignment.employeeId,
            assignedAt: assignment.assignedAt
          };
        }
        return customer;
      });
      
      setCustomersList(updatedCustomers);
    } catch (error) {
      console.error('Fejl ved hentning af kundetildelinger:', error);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    
    if (!newMemberEmail) {
      setError(t('organization', 'notifications.error.emailRequired'));
      return;
    }
    
    if (!organizationId) {
      setError(t('organization', 'notifications.error.noOrganization'));
      return;
    }
    
    // Tjek om der er tilgængelige licenser
    if (getAvailableLicenses() <= 0) {
      // Vis BuyLicenseModal i stedet
      setIsInviteModalOpen(false);
      setIsBuyLicenseModalOpen(true);
      return;
    }
    
    setInviteLoading(true);
    setError('');
    
    try {
      // Tjek om brugeren allerede er medlem eller inviteret
      const memberQuery = query(
        collection(db, 'organizations', organizationId, 'members'),
        where('email', '==', newMemberEmail)
      );
      
      const memberSnapshot = await getDocs(memberQuery);
      
      if (!memberSnapshot.empty) {
        setError(t('organization', 'notifications.error.memberExists'));
        setInviteLoading(false);
        return;
      }
      
      // Tjek om brugeren allerede er inviteret
      const invitationQuery = query(
        collection(db, 'organizations', organizationId, 'invitations'),
        where('email', '==', newMemberEmail),
        where('status', '==', 'sent')
      );
      
      const invitationSnapshot = await getDocs(invitationQuery);
      
      if (!invitationSnapshot.empty) {
        setError(t('organization', 'notifications.error.invitationExists'));
        setInviteLoading(false);
        return;
      }
      
      // Hent organisationens navn
      const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
      const orgName = orgDoc.exists() ? orgDoc.data().name : 'Nuvik.io Organisation';
      
      // Generér et unikt token til invitation
      const token = Math.random().toString(36).substring(2, 15) + 
                    Math.random().toString(36).substring(2, 15);
      
      // Opret invitation i Firestore - brug den aktuelle værdi af memberRole
      await addDoc(collection(db, 'organizations', organizationId, 'invitations'), {
        email: newMemberEmail,
        role: memberRole === 'customer' ? 'customer' : 'employee', // Gem rollen
        userType: memberRole, // Gem også brugertypen
        status: 'pending',
        token: token,
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName || currentUser.email.split('@')[0],
        organizationName: orgName,
        createdAt: serverTimestamp()
      });
      
      // Nulstil form og vis success message
      setNewMemberEmail('');
      setMemberRole('employee'); // Nulstil rolle til standard
      setInviteLoading(false);
      setIsInviteModalOpen(false);
      setSuccessMessage(t('organization', 'notifications.success.invitationSent', { email: newMemberEmail }));
      
      // Fjern success message efter 3 sekunder
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Fejl ved invitation af medlem:', error);
      setError(t('organization', 'notifications.error.general'));
      setInviteLoading(false);
    }
  };

  const handleRemoveMember = async (memberId, email) => {
    if (window.confirm(t('organization', 'messages.confirmRemoveMember', { email }))) {
      try {
        if (!organizationId) {
          throw new Error('Ingen organisation fundet');
        }
        
        // Hent medlemmets email-adresse hvis den ikke er med som parameter
        let memberEmail = email;
        if (!memberEmail) {
          const memberDoc = await getDoc(doc(db, 'organizations', organizationId, 'members', memberId));
          if (memberDoc.exists()) {
            memberEmail = memberDoc.data().email;
          }
        }
        
        // Markér medlemmet som fjernet
        await updateDoc(doc(db, 'organizations', organizationId, 'members', memberId), {
          status: 'removed',
          removedAt: serverTimestamp(),
          removedBy: currentUser.uid
        });
        
        // Hvis vi har email-adressen, fjern også eventuelle invitationer til denne email
        if (memberEmail) {
          // Find invitationer med samme email
          const invitationsRef = collection(db, 'organizations', organizationId, 'invitations');
          const invitationQuery = query(invitationsRef, where('email', '==', memberEmail));
          const invitationSnapshot = await getDocs(invitationQuery);
          
          // Slet alle fundne invitationer
          const deletePromises = [];
          invitationSnapshot.forEach(doc => {
            console.log(`Sletter invitation ${doc.id} for email ${memberEmail}`);
            deletePromises.push(deleteDoc(doc.ref));
          });
          
          // Vent på at alle sletninger er gennemført
          if (deletePromises.length > 0) {
            await Promise.all(deletePromises);
            console.log(`Slettet ${deletePromises.length} invitationer for ${memberEmail}`);
          }
        }
        
        // Opdater lokalt state
        setMembers(members.filter(member => member.id !== memberId));
        
        setSuccessMessage(t('organization', 'notifications.success.memberRemoved'));
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error('Fejl ved fjernelse af medlem:', error);
        setError(t('organization', 'notifications.error.general'));
      }
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (window.confirm(t('organization', 'messages.confirmCancelInvitation'))) {
      try {
        if (!organizationId) {
          throw new Error('Ingen organisation fundet');
        }
        
        console.log('Annullerer invitation:', invitationId, 'i organisation:', organizationId);
        
        // Fjern invitationen helt i stedet for bare at ændre status
        const invitationRef = doc(db, 'organizations', organizationId, 'invitations', invitationId);
        await deleteDoc(invitationRef);
        
        // Opdater lokalt state
        setMembers(members.filter(member => member.id !== invitationId));
        
        setSuccessMessage(t('organization', 'notifications.success.invitationCancelled'));
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error('Fejl ved annullering af invitation:', error);
        setError(t('organization', 'notifications.error.general'));
      }
    }
  };

  const handleBillingClick = async () => {
    try {
      if (!currentUser) return;
      
      const response = await fetch(
        `${process.env.REACT_APP_STRIPE_CREATE_PORTAL_SESSION_URL}/${currentUser.uid}?email=${encodeURIComponent(currentUser.email)}`
      );
      
      if (!response.ok) {
        throw new Error(`Server svarede med status: ${response.status}`);
      }
      
      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Fejl ved åbning af kundeportal:', error);
      setError(t('organization', 'notifications.error.billingPortal'));
    }
  };

  // Gruppér abonnementer efter type
  const groupedSubscriptions = useMemo(() => {
    if (!subscriptionData?.subscriptions) return {};
    
    return subscriptionData.subscriptions.reduce((acc, sub) => {
      const type = sub.product.name;
      if (!acc[type]) acc[type] = [];
      acc[type].push(sub);
      return acc;
    }, {});
  }, [subscriptionData]);

  // Licens håndtering
  const handleAddLicenses = async (quantity) => {
    try {
      // Implementer logik for at tilføje licenser
      console.log('Tilføjer licenser:', quantity);
    } catch (error) {
      console.error('Fejl ved tilføjelse af licenser:', error);
    }
  };

  const getTotalLicenses = () => {
    // Hent direkte fra subscriptionData.totalSeats hvis tilgængelig
    if (subscriptionData?.totalSeats) {
      console.log(`Bruger totalSeats direkte fra subscriptionData: ${subscriptionData.totalSeats}`);
      return subscriptionData.totalSeats;
    }
    
    // Alternativt beregn fra allSubscriptions
    if (subscriptionData?.allSubscriptions && Array.isArray(subscriptionData.allSubscriptions)) {
      const stripeTotal = subscriptionData.allSubscriptions.reduce((total, sub) => 
        total + (sub.seats || 1), 0);
      console.log(`Beregnet ${stripeTotal} licenser fra subscriptionData.allSubscriptions`);
      return stripeTotal;
    }
    
    // Fallback til Firestore licenser hvis ingen Stripe data
    console.log(`Ingen Stripe data, bruger antal licenser i Firestore: ${organizationLicenses.length}`);
    return Math.max(stripeTotalLicenses, organizationLicenses.length);
  };

  const getAssignedLicenses = () => {
    // Tæl aktive medlemmer i members-listen
    const activeMembers = members.filter(member => 
      !member.isInvitation && member.status === 'active'
    ).length;
    
    console.log(`Antal aktive medlemmer: ${activeMembers}`);
    return activeMembers;
  };

  const getAvailableLicenses = () => {
    const total = getTotalLicenses();
    const assigned = getAssignedLicenses();
    const available = Math.max(0, total - assigned);
    
    console.log(`Total licenser: ${total}, Tildelte licenser: ${assigned}, Tilgængelige licenser: ${available}`);
    return available;
  };

  const getLicenseTypeName = (type) => {
    // Implementer logik for at få licenstype navn
    const types = {
      basic: 'Basic',
      pro: 'Professional',
      enterprise: 'Enterprise'
    };
    return types[type] || type;
  };

  // Tjek og tildel licens til den nuværende bruger, hvis de ikke allerede har en
  const checkAndAssignLicenseToCurrentUser = async () => {
    if (!organizationId || !currentUser) return;
    
    try {
      // Tjek om den nuværende bruger allerede har en licens
      const licensesRef = collection(db, 'organizations', organizationId, 'licenses');
      const licensesQuery = query(
        licensesRef,
        where('assignedTo', '==', currentUser.uid),
        where('status', '==', 'active')
      );
      
      const licensesSnapshot = await getDocs(licensesQuery);
      
      if (licensesSnapshot.empty) {
        // Ingen licens fundet - tjek om der er ledige licenser
        const unusedLicensesQuery = query(
          licensesRef,
          where('assignedTo', '==', null),
          where('status', '==', 'active'),
          limit(1)
        );
        
        const unusedLicensesSnapshot = await getDocs(unusedLicensesQuery);
        
        if (!unusedLicensesSnapshot.empty) {
          // Tildel en ledig licens til den nuværende bruger
          const licenseDoc = unusedLicensesSnapshot.docs[0];
          await updateDoc(doc(db, 'organizations', organizationId, 'licenses', licenseDoc.id), {
            assignedTo: currentUser.uid,
            assignedEmail: currentUser.email,
            assignedAt: serverTimestamp()
          });
          console.log('Licens tildelt til den nuværende bruger');
        } else {
          console.log('Ingen ledige licenser tilgængelige for den nuværende bruger');
        }
      } else {
        console.log('Nuværende bruger har allerede en licens');
      }
    } catch (error) {
      console.error('Fejl ved tildeling af licens til nuværende bruger:', error);
    }
  };

  // Kald denne funktion når komponenten indlæses
  useEffect(() => {
    if (organizationId && currentUser) {
      checkAndAssignLicenseToCurrentUser();
    }
  }, [organizationId, currentUser]);

  const handleChangeLicense = async (userId, newType) => {
    try {
      // Implementer logik for at ændre licenstype
      console.log('Ændrer licens for bruger:', userId, 'til:', newType);
    } catch (error) {
      console.error('Fejl ved ændring af licens:', error);
    }
  };

  const handleRemoveLicense = async (userId) => {
    try {
      // Implementer logik for at fjerne licens
      console.log('Fjerner licens fra bruger:', userId);
    } catch (error) {
      console.error('Fejl ved fjernelse af licens:', error);
    }
  };

  const handleAssignLicense = async (userId, type) => {
    try {
      // Implementer logik for at tildele licens
      console.log('Tildeler licens til bruger:', userId, 'type:', type);
    } catch (error) {
      console.error('Fejl ved tildeling af licens:', error);
    }
  };

  const handleAssignLicenseSubmit = async (data) => {
    await handleAssignLicense(data.userId, data.type);
    setIsAssignModalOpen(false);
  };

  // Abonnements håndtering
  const getStatusText = (status) => {
    const statusMap = {
      active: 'Aktiv',
      trialing: 'Prøveperiode',
      canceled: 'Opsagt'
    };
    return statusMap[status] || status;
  };

  const formatPrice = (amount, currency = 'DKK') => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const handleUpdateSubscription = async (subscriptionId, updates) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_STRIPE_UPDATE_SUBSCRIPTION_URL}/${subscriptionId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates)
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke opdatere abonnement');
      }

      // Genindlæs abonnementsdata
      await fetchSubscriptionData();
      setIsUpdateModalOpen(false);
    } catch (error) {
      console.error('Fejl ved opdatering af abonnement:', error);
      setError(t('organization', 'notifications.error.general'));
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm(t('organization', 'messages.confirmCancelSubscription'))) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_STRIPE_CANCEL_SUBSCRIPTION_URL}/${subscriptionId}`,
        {
          method: 'POST'
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke opsige abonnement');
      }

      // Genindlæs abonnementsdata
      await fetchSubscriptionData();
    } catch (error) {
      console.error('Fejl ved opsigelse af abonnement:', error);
      setError(t('organization', 'notifications.error.general'));
    }
  };

  const handleUpdateSubscriptionSubmit = async (data) => {
    await handleUpdateSubscription(selectedSubscription?.id, data);
    setIsUpdateModalOpen(false);
  };

  // Lyt til menu collapse status
  useEffect(() => {
    const handleMenuCollapse = (e) => {
      if (e.detail) {
        setIsMenuCollapsed(e.detail.isCollapsed);
      }
    };

    window.addEventListener('menuCollapse', handleMenuCollapse);
    return () => window.removeEventListener('menuCollapse', handleMenuCollapse);
  }, []);

  // Implementer en handler for at købe licenser
  const handleBuyLicenses = async (quantity) => {
    try {
      setLoading(true);
      setError('');
      
      if (!currentUser) {
        throw new Error('Du skal være logget ind for at købe licenser');
      }
      
      // Brug Stripe API til at købe licenser
      const response = await fetch(process.env.REACT_APP_STRIPE_CREATE_CHECKOUT_SESSION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          billingInterval: 'month',
          quantities: { basic: quantity },
          withTrial: false, // Ingen prøveperiode ved tilkøb af ekstra licenser
          customerEmail: currentUser.email,
          firebaseUid: currentUser.uid,
          updateExisting: true, // Angiv at vi vil opgradere eksisterende abonnement
          existingSubscriptionId: subscriptionData?.subscriptionId // Send eksisterende abonnements-ID
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Der opstod en fejl ved oprettelse af betalingsside');
      }
      
      const { url } = await response.json();
      window.open(url, '_blank');
      
      setIsBuyLicenseModalOpen(false);
    } catch (error) {
      console.error('Fejl ved køb af licenser:', error);
      setError(t('organization', 'notifications.error.general'));
    } finally {
      setLoading(false);
    }
  };

  // Funktion til at tildele en kunde til en medarbejder
  const assignCustomerToEmployee = async (customerId, employeeId) => {
    if (!organizationId || !customerId || !employeeId) {
      setError(t('organization', 'notifications.error.general'));
      return;
    }
    
    try {
      // Tjek om der allerede findes en tildeling for denne kunde
      const assignmentsRef = collection(db, 'organizations', organizationId, 'customerAssignments');
      const q = query(assignmentsRef, where('customerId', '==', customerId));
      const existingAssignments = await getDocs(q);
      
      if (!existingAssignments.empty) {
        // Opdater eksisterende tildeling
        const assignmentDoc = existingAssignments.docs[0];
        await updateDoc(doc(db, 'organizations', organizationId, 'customerAssignments', assignmentDoc.id), {
          employeeId: employeeId,
          updatedAt: serverTimestamp()
        });
        
        console.log(`Kundetildeling opdateret: Kunde ${customerId} tildelt til medarbejder ${employeeId}`);
      } else {
        // Opret ny tildeling
        await addDoc(collection(db, 'organizations', organizationId, 'customerAssignments'), {
          customerId: customerId,
          employeeId: employeeId,
          assignedAt: serverTimestamp(),
          assignedBy: currentUser.uid
        });
        
        console.log(`Ny kundetildeling oprettet: Kunde ${customerId} tildelt til medarbejder ${employeeId}`);
      }
      
      // Vis succesbesked
      setSuccessMessage(t('organization', 'notifications.success.customerAssigned'));
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Luk modal
      setIsAssignCustomerModalOpen(false);
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Fejl ved tildeling af kunde:', error);
      setError(t('organization', 'notifications.error.general'));
    }
  };

  // Funktion til at fjerne tildeling
  const handleRemoveAssignment = async (customerId) => {
    if (!organizationId || !customerId) {
      setError(t('organization', 'notifications.error.general'));
      return;
    }
    
    try {
      // Find tildelingen baseret på kunde-ID
      const assignmentsRef = collection(db, 'organizations', organizationId, 'customerAssignments');
      const q = query(assignmentsRef, where('customerId', '==', customerId));
      const assignmentsSnapshot = await getDocs(q);
      
      if (assignmentsSnapshot.empty) {
        console.log('Ingen tildeling fundet for denne kunde');
        return;
      }
      
      // Slet tildelingen
      const assignmentDoc = assignmentsSnapshot.docs[0];
      await deleteDoc(doc(db, 'organizations', organizationId, 'customerAssignments', assignmentDoc.id));
      
      console.log(`Kundetildeling fjernet for kunde ${customerId}`);
      
      // Opdater kundelisten lokalt
      const updatedCustomers = customersList.map(customer => {
        if (customer.id === customerId || customer.userId === customerId) {
          const { assignedTo, assignmentId, assignedAt, ...rest } = customer;
          return rest;
        }
        return customer;
      });
      
      setCustomersList(updatedCustomers);
      
      // Vis succesbesked
      setSuccessMessage(t('organization', 'notifications.success.assignmentRemoved'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Fejl ved fjernelse af kundetildeling:', error);
      setError(t('organization', 'notifications.error.general'));
    }
  };

  // Funktion til at håndtere klik på "Tildel" knappen
  const handleAssignCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsAssignCustomerModalOpen(true);
  };

  // Hjælpefunktion til at finde medarbejdernavn baseret på ID
  const getEmployeeNameById = (employeeId) => {
    const employee = employeesList.find(emp => emp.id === employeeId || emp.userId === employeeId);
    return employee ? (employee.name || employee.email) : 'Ukendt medarbejder';
  };

  // Tilføj denne funktion til at hente licenser fra Firestore
  const fetchOrganizationLicenses = async (orgId) => {
    if (!orgId) return;
    
    try {
      console.log("Organization.js: Henter licenser for organisation", orgId);
      console.log("Organization.js: Bruger har organisationID", organizationId);

      const licensesRef = collection(db, 'organizations', orgId, 'licenses');
      const licensesSnapshot = await getDocs(licensesRef);
      
      const licenses = [];
      licensesSnapshot.forEach(doc => {
        licenses.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`Fandt ${licenses.length} licenser i Firestore for organisation ${orgId}, heraf ${licenses.filter(l => l.assignedTo).length} tildelte`);
      setOrganizationLicenses(licenses);
    } catch (error) {
      console.error('Fejl ved hentning af licenser:', error);
    }
  };

  if (loading) {
    return (
      <div className="org-loading">
        <div className="org-spinner"></div>
        <p>{t('organization', 'common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="org-error-message">
        <h2>{t('organization', 'common.error')}</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Menu onLogout={onLogout} />
      <div className={`org-container ${isMenuCollapsed ? 'menu-collapsed' : ''}`}>
        <div className="org-header">
          <h1>{t('organization', 'header.title')}</h1>
          {!isOrgAdmin && (
            <div className="org-role-badge">
              <i className="fas fa-user"></i>
              <span>{t('organization', 'header.employeeRole')}</span>
            </div>
          )}
          <p className="org-description">
            {isOrgAdmin 
              ? t('organization', 'header.adminDescription')
              : t('organization', 'header.employeeDescription')}
          </p>
        </div>

        {successMessage && (
          <div className="org-success-message">
            <i className="fas fa-check-circle"></i>
            <p>{successMessage}</p>
          </div>
        )}

        <div className="org-grid">
          {/* LinkedIn Poster Licenser Kort */}
          <div className="org-card subscription-card">
            <div className="org-card-header">
              <h2>{t('organization', 'subscription.card.title')}</h2>
              {subscriptionData?.status && (
                <span className={`org-status-badge ${subscriptionData.status}`}>
                  {subscriptionData.status === 'active' ? t('organization', 'subscription.card.status.active') : 
                   subscriptionData.status === 'trialing' ? t('organization', 'subscription.card.status.trialing') : 
                   t('organization', 'subscription.card.status.inactive')}
                </span>
              )}
            </div>
            <div className="org-card-content">
              <div className="org-plan-details">
                <div className="org-plan-name">
                  <span className="label">{t('organization', 'subscription.card.currentPlan')}</span>
                  <h3 className="value">{subscriptionData?.planName || t('organization', 'subscription.card.noPlan')}</h3>
                </div>
                
                <div className="org-plan-limits">
                  <div className="limit-item">
                    <div className="limit-icon">
                      <i className="fas fa-users"></i>
                    </div>
                    <div className="limit-details">
                      <span className="limit-label">{t('organization', 'subscription.card.licenseInfo.total')}</span>
                      <span className="limit-value">{getTotalLicenses()}</span>
                    </div>
                  </div>

                  <div className="limit-item">
                    <div className="limit-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="limit-details">
                      <span className="limit-label">{t('organization', 'subscription.card.licenseInfo.assigned')}</span>
                      <span className="limit-value">{getAssignedLicenses()}</span>
                    </div>
                  </div>

                  <div className="limit-item">
                    <div className="limit-icon">
                      <i className="fas fa-plus-circle"></i>
                    </div>
                    <div className="limit-details">
                      <span className="limit-label">{t('organization', 'subscription.card.licenseInfo.available')}</span>
                      <span className="limit-value">{getAvailableLicenses()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {subscriptionData?.status === 'trialing' && (
                <div className="org-trial-info">
                  <i className="fas fa-clock"></i>
                  <p>
                    {t('organization', 'subscription.card.trial.info')}{' '}
                    <span className="org-date">
                      {new Date(subscriptionData.trialEnd).toLocaleDateString('da-DK')}
                    </span>
                  </p>
                </div>
              )}

              {isOrgAdmin && (
                <div className="org-subscription-actions">
                  <button 
                    className="org-subscription-button primary"
                    onClick={() => setIsBuyLicenseModalOpen(true)}
                  >
                    <i className="fas fa-plus"></i>
                    {t('organization', 'subscription.card.actions.addLicenses')}
                  </button>
                  <button 
                    className="org-subscription-button tertiary"
                    onClick={handleBillingClick}
                  >
                    <i className="fas fa-credit-card"></i>
                    {t('organization', 'subscription.card.actions.manageBilling')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Team Kort */}
          <div className="org-card team-card">
            <div className="org-card-header">
              <h2>{t('organization', 'team.card.title')}</h2>
              {isOrgAdmin && (
                <button 
                  className="org-add-member-button"
                  onClick={() => setIsInviteModalOpen(true)}
                  disabled={getAvailableLicenses() <= 0}
                >
                  <i className="fas fa-user-plus"></i>
                  {t('organization', 'team.card.inviteButton')}
                </button>
              )}
            </div>
            <div className="org-card-content">
              {members.length === 0 ? (
                <div className="org-empty-state">
                  <div className="org-empty-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <p className="org-empty-text">
                    {isOrgAdmin 
                      ? t('organization', 'team.card.emptyState.admin')
                      : t('organization', 'team.card.emptyState.employee')}
                  </p>
                </div>
              ) : (
                <div className="org-members-list">
                  {members.map((member) => (
                    <div key={member.id} className={`org-member-item ${member.isCurrentUser ? 'org-current-user' : ''}`}>
                      <div className="org-member-avatar">
                        {member.name ? member.name.charAt(0).toUpperCase() : 
                        (member.email ? member.email.charAt(0).toUpperCase() : 'U')}
                      </div>
                      <div className="org-member-info">
                        <p className="org-member-email">
                          {member.email} {member.isCurrentUser && t('organization', 'team.card.memberInfo.you')}
                        </p>
                        <div className="org-member-details">
                          <span className="org-member-role">{member.role === 'admin' ? t('organization', 'team.card.memberInfo.admin') : t('organization', 'team.card.memberInfo.member')}</span>
                          <span className={`org-member-status ${member.status === 'active' ? 'aktiv' : 'inviteret'}`}>
                            {member.status === 'active' ? t('organization', 'team.card.memberInfo.status.active') : t('organization', 'team.card.memberInfo.status.invited')}
                          </span>
                          {member.isInvitation && <span className="org-invitation-badge">{t('organization', 'team.card.memberInfo.invitationBadge')}</span>}
                        </div>
                      </div>
                      {isOrgAdmin && !member.isCurrentUser && (
                        <button 
                          className="org-member-remove" 
                          onClick={() => {
                            console.log("Fjerner:", member);
                            if (member.isInvitation) {
                              console.log("Håndterer som invitation");
                              handleCancelInvitation(member.id);
                            } else {
                              console.log("Håndterer som medlem");
                              handleRemoveMember(member.id, member.email);
                            }
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Kunde Håndtering Kort */}
          <div className="org-card customer-management-card">
            <div className="org-card-header">
              <h2>{t('organization', 'customerManagement.card.title')}</h2>
              <span className="org-subheader">
                {isOrgAdmin 
                  ? t('organization', 'customerManagement.card.adminSubheader')
                  : t('organization', 'customerManagement.card.employeeSubheader')}
              </span>
            </div>
            <div className="org-card-content">
              {customersList.length === 0 ? (
                <div className="org-empty-state">
                  <div className="org-empty-icon">
                    <i className="fas fa-user-friends"></i>
                  </div>
                  <p className="org-empty-text">
                    {isOrgAdmin 
                      ? t('organization', 'customerManagement.card.emptyState.admin')
                      : t('organization', 'customerManagement.card.emptyState.employee')}
                  </p>
                </div>
              ) : (
                <div className="org-table-container">
                  <table className="org-table customer-table">
                    <thead>
                      <tr>
                        <th>{t('organization', 'customerManagement.card.tableHeaders.customer')}</th>
                        <th>{t('organization', 'customerManagement.card.tableHeaders.email')}</th>
                        <th>{t('organization', 'customerManagement.card.tableHeaders.status')}</th>
                        <th>{t('organization', 'customerManagement.card.tableHeaders.assignedTo')}</th>
                        {isOrgAdmin && <th>{t('organization', 'customerManagement.card.tableHeaders.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {customersList.map((customer) => (
                        <tr key={customer.id} className="org-customer-row">
                          <td>
                            <div className="org-customer-info">
                              <div className="org-customer-avatar">
                                {customer.name ? customer.name.charAt(0).toUpperCase() : 
                                (customer.email ? customer.email.charAt(0).toUpperCase() : 'K')}
                              </div>
                              <span>{customer.name || customer.email.split('@')[0]}</span>
                            </div>
                          </td>
                          <td>{customer.email}</td>
                          <td>
                            <span className={`org-status-badge ${customer.status}`}>
                              {customer.status === 'active' ? t('organization', 'customerManagement.card.status.active') : t('organization', 'customerManagement.card.status.invited')}
                            </span>
                          </td>
                          <td>
                            {customer.assignedTo ? (
                              <div className="org-employee-badge">
                                <i className="fas fa-user-tie"></i>
                                <span>{getEmployeeNameById(customer.assignedTo)}</span>
                              </div>
                            ) : (
                              <span className="org-not-assigned">{t('organization', 'customerManagement.card.notAssigned')}</span>
                            )}
                          </td>
                          {isOrgAdmin && (
                            <td>
                              <div className="org-actions">
                                <button 
                                  className="org-action-button"
                                  onClick={() => handleAssignCustomer(customer)}
                                  title={t('organization', 'customerManagement.card.actions.assign')}
                                >
                                  <i className="fas fa-user-plus"></i>
                                </button>
                                {customer.assignedTo && (
                                  <button 
                                    className="org-action-button remove"
                                    onClick={() => handleRemoveAssignment(customer.id || customer.userId)}
                                    title={t('organization', 'customerManagement.card.actions.removeAssignment')}
                                  >
                                    <i className="fas fa-user-slash"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modaler til forskellige handlinger */}
        <AssignLicenseModal 
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          onSubmit={handleAssignLicenseSubmit}
          availableLicenses={getAvailableLicenses(selectedProductType)}
        />
        
        <UpdateSubscriptionModal
          isOpen={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          onSubmit={handleUpdateSubscriptionSubmit}
          subscription={selectedSubscription}
        />
        
        <InviteMemberModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          onSubmit={handleInviteMember}
          email={newMemberEmail}
          setEmail={setNewMemberEmail}
          memberRole={memberRole}
          setMemberRole={setMemberRole}
          loading={inviteLoading}
          availableLicenses={getAvailableLicenses()}
          error={error}
          setError={setError}
        />
        
        <BuyLicenseModal
          isOpen={isBuyLicenseModalOpen}
          onClose={() => setIsBuyLicenseModalOpen(false)}
          onSubmit={handleBuyLicenses}
          currentLicenses={getTotalLicenses()}
        />

        <AssignCustomerModal
          isOpen={isAssignCustomerModalOpen}
          onClose={() => setIsAssignCustomerModalOpen(false)}
          onSubmit={assignCustomerToEmployee}
          customer={selectedCustomer}
          employeesList={employeesList}
        />
      </div>
    </div>
  );
};

const AssignLicenseModal = ({ isOpen, onClose, onSubmit, availableLicenses }) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedType, setSelectedType] = useState('basic');
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="org-modal-overlay">
      <div className="org-modal">
        <div className="org-modal-header">
          <h3>{t('organization', 'modals.assignLicense.title')}</h3>
          <button className="org-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="org-modal-body">
          <div className="org-form-group">
            <label>{t('organization', 'modals.assignLicense.email')}</label>
            <input
              type="email"
              className="org-input"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              placeholder="Indtast brugerens email"
            />
          </div>
          <div className="org-form-group">
            <label>{t('organization', 'modals.assignLicense.licenseType')}</label>
            <select 
              className="org-input"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="basic">{t('organization', 'modals.assignLicense.licenseTypes.basic')}</option>
              <option value="pro">{t('organization', 'modals.assignLicense.licenseTypes.pro')}</option>
              <option value="enterprise">{t('organization', 'modals.assignLicense.licenseTypes.enterprise')}</option>
            </select>
          </div>
          <p>{t('organization', 'modals.assignLicense.availableLicenses')} {availableLicenses}</p>
        </div>
        <div className="org-modal-footer">
          <button className="org-button-cancel" onClick={onClose}>{t('organization', 'common.cancel')}</button>
          <button 
            className="org-button-invite"
            onClick={() => onSubmit({ userId: selectedUser, type: selectedType })}
            disabled={!selectedUser || availableLicenses <= 0}
          >
            {t('organization', 'modals.assignLicense.title')}
          </button>
        </div>
      </div>
    </div>
  );
};

const UpdateSubscriptionModal = ({ isOpen, onClose, onSubmit, subscription }) => {
  const [quantity, setQuantity] = useState(subscription?.quantity || 1);
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="org-modal-overlay">
      <div className="org-modal">
        <div className="org-modal-header">
          <h3>{t('organization', 'modals.updateSubscription.title')}</h3>
          <button className="org-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="org-modal-body">
          <div className="org-form-group">
            <label>{t('organization', 'modals.updateSubscription.quantity')}</label>
            <input
              type="number"
              min="1"
              className="org-input"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value))}
            />
          </div>
          <div className="subscription-preview">
            <p>{t('organization', 'modals.updateSubscription.preview.plan')} {subscription?.plan?.name}</p>
            <p>{t('organization', 'modals.updateSubscription.preview.pricePerLicense')} {subscription?.plan?.amount} DKK</p>
            <p>{t('organization', 'modals.updateSubscription.preview.total')} {subscription?.plan?.amount * quantity} DKK</p>
          </div>
        </div>
        <div className="org-modal-footer">
          <button className="org-button-cancel" onClick={onClose}>{t('organization', 'common.cancel')}</button>
          <button 
            className="org-button-invite"
            onClick={() => onSubmit({ quantity })}
          >
            {t('organization', 'modals.updateSubscription.title')}
          </button>
        </div>
      </div>
    </div>
  );
};

const InviteMemberModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  email, 
  setEmail, 
  memberRole,
  setMemberRole,
  loading, 
  availableLicenses, 
  error, 
  setError 
}) => {
  const { t } = useLanguage();
  
  if (!isOpen) return null;

  return (
    <div className="org-modal-overlay">
      <div className="org-modal">
        <div className="org-modal-header">
          <h3>{t('organization', 'modals.inviteMember.title')}</h3>
          <button className="org-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="org-modal-body">
          {error && <div className="org-error-message">{error}</div>}
          
          <div className="org-form-group">
            <label>{t('organization', 'modals.inviteMember.email')}</label>
            <input
              type="email"
              className="org-input"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              placeholder={t('organization', 'modals.inviteMember.emailPlaceholder')}
            />
          </div>
          
          <div className="org-form-group">
            <label>{t('organization', 'modals.inviteMember.role')}</label>
            <select 
              className="org-input"
              value={memberRole}
              onChange={(e) => setMemberRole(e.target.value)}
            >
              <option value="employee">{t('organization', 'modals.inviteMember.roles.employee')}</option>
              <option value="customer">{t('organization', 'modals.inviteMember.roles.customer')}</option>
            </select>
          </div>
          
          <div className="invitation-info">
            <i className="fas fa-info-circle"></i>
            <p>
              {t('organization', 'modals.inviteMember.info')}
              {memberRole === 'employee' 
                ? t('organization', 'modals.inviteMember.employeeInfo')
                : t('organization', 'modals.inviteMember.customerInfo')}
            </p>
          </div>
          
          <p>{t('organization', 'modals.inviteMember.availableLicenses')} {availableLicenses}</p>
        </div>
        <div className="org-modal-footer">
          <button className="org-button-cancel" onClick={onClose}>{t('organization', 'common.cancel')}</button>
          <button 
            className="org-button-invite"
            onClick={onSubmit}
            disabled={!email || loading || availableLicenses <= 0}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {t('organization', 'modals.inviteMember.sending')}
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i>
                {t('organization', 'modals.inviteMember.send')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const BuyLicenseModal = ({ isOpen, onClose, onSubmit, currentLicenses }) => {
  const [quantity, setQuantity] = useState(1);
  const { t } = useLanguage();
  
  if (!isOpen) return null;
  
  return (
    <div className="org-modal-overlay">
      <div className="org-modal">
        <div className="org-modal-header">
          <h3>{t('organization', 'modals.buyLicense.title')}</h3>
          <button className="org-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="org-modal-body">
          <div className="invitation-info">
            <i className="fas fa-info-circle"></i>
            <p>
              {t('organization', 'modals.buyLicense.info', { currentLicenses })}
            </p>
          </div>
          
          <div className="org-form-group">
            <label>{t('organization', 'modals.buyLicense.quantity')}</label>
            <input
              type="number"
              min="1"
              className="org-input"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            />
          </div>
          
          <div className="subscription-preview">
            <p>{t('organization', 'modals.buyLicense.pricing.info')}</p>
            <p>{t('organization', 'modals.buyLicense.pricing.price')}</p>
            <p>{t('organization', 'modals.buyLicense.pricing.total', { total: 1490 * quantity })}</p>
          </div>
        </div>
        <div className="org-modal-footer">
          <button className="org-button-cancel" onClick={onClose}>{t('organization', 'common.cancel')}</button>
          <button 
            className="org-button-invite"
            onClick={() => onSubmit(quantity)}
          >
            <i className="fas fa-shopping-cart"></i>
            {t('organization', 'modals.buyLicense.buy')}
          </button>
        </div>
      </div>
    </div>
  );
};

const AssignCustomerModal = ({ isOpen, onClose, onSubmit, customer, employeesList }) => {
  const [selectedEmployee, setSelectedEmployee] = useState(customer?.assignedTo || '');
  const { t } = useLanguage();

  useEffect(() => {
    if (customer) {
      setSelectedEmployee(customer.assignedTo || '');
    }
  }, [customer]);

  if (!isOpen || !customer) return null;

  return (
    <div className="org-modal-overlay">
      <div className="org-modal">
        <div className="org-modal-header">
          <h3>{t('organization', 'modals.assignCustomer.title', { customerName: customer.name || customer.email })}</h3>
          <button className="org-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="org-modal-body">
          <div className="org-form-group">
            <label>{t('organization', 'modals.assignCustomer.selectEmployee')}</label>
            <select 
              className="org-input"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="">{t('organization', 'modals.assignCustomer.noEmployee')}</option>
              {employeesList.map((employee) => (
                <option key={employee.id || employee.userId} value={employee.id || employee.userId}>
                  {employee.name || employee.email} {employee.isCurrentUser ? t('organization', 'team.card.memberInfo.you') : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="org-modal-footer">
          <button className="org-button-cancel" onClick={onClose}>{t('organization', 'common.cancel')}</button>
          <button 
            className="org-button-invite"
            onClick={() => onSubmit(customer.id || customer.userId, selectedEmployee)}
            disabled={!selectedEmployee}
          >
            {t('organization', 'modals.assignCustomer.assign')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Organization; 